import { useTranslation } from 'react-i18next';
import { translateBackendError } from '@/api/translateBackendError';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { useDownloadWorkspaceExportMutation } from '@/hooks/useDownloadWorkspaceExportMutation';
import { useRequestWorkspaceExportMutation } from '@/hooks/useRequestWorkspaceExportMutation';
import { useWorkspaceExportQuery } from '@/hooks/useWorkspaceExportQuery';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { WorkspaceExportStatus } from './WorkspaceExportStatus';

export function WorkspaceExportSection() {
  const { t } = useTranslation('workspaceData');
  const exportQuery = useWorkspaceExportQuery();
  const requestExportMutation = useRequestWorkspaceExportMutation();
  const downloadExportMutation = useDownloadWorkspaceExportMutation();

  async function requestExport() {
    try {
      await requestExportMutation.mutateAsync();
      showSuccessNotification(t('export.requested'));
    } catch (requestError) {
      showErrorNotification(translateBackendError(t, requestError));
    }
  }

  async function downloadExport(exportId: string) {
    try {
      await downloadExportMutation.mutateAsync(exportId);
    } catch (downloadError) {
      showErrorNotification(translateBackendError(t, downloadError));
    }
  }

  function renderBody() {
    if (exportQuery.isLoading === true) {
      return (
        <div className="py-6 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      );
    }
    if (exportQuery.isError === true || exportQuery.data === undefined) {
      return (
        <QueryErrorState
          onRetry={function retry() {
            exportQuery.refetch();
          }}
        />
      );
    }
    return (
      <WorkspaceExportStatus
        workspaceExport={exportQuery.data}
        isRequesting={requestExportMutation.isPending}
        isDownloading={downloadExportMutation.isPending}
        onRequest={requestExport}
        onDownload={downloadExport}
      />
    );
  }

  return (
    <Card padding="lg">
      <h2 className="text-sm font-bold text-zooindigo">{t('export.title')}</h2>
      <p className="text-[13px] text-zoomuted mt-2 max-w-3xl">{t('export.description')}</p>
      <p className="text-[11px] text-zoomuted mt-2 max-w-3xl">{t('export.contentsHint')}</p>
      <div className="mt-5">{renderBody()}</div>
    </Card>
  );
}
